import axios from 'axios';
import { getAccessToken } from '../auth/oidcClient';

// Detectar se está rodando via ngrok e ajustar a URL da API se necessário
const getApiBaseURL = () => {
  const envUrl = import.meta.env.VITE_API_URL;
  
  // Se a URL da API for localhost mas estamos acessando via ngrok, tentar detectar
  if (envUrl && envUrl.includes('localhost') && window.location.hostname.includes('ngrok')) {
    console.warn('[API] Detectado acesso via ngrok, mas API_URL aponta para localhost');
    console.warn('[API] Configure VITE_API_URL com a URL do ngrok da API no arquivo .env.development');
  }
  
  return envUrl || 'https://localhost:5010';
};

const api = axios.create({
  baseURL: getApiBaseURL(),
  headers: {
    'Content-Type': 'application/json'
  },
  // Timeout aumentado para conexões mais lentas (celular)
  timeout: 30000
});

console.log('[API] Cliente configurado com baseURL:', api.defaults.baseURL);
console.log('[API] Origem atual:', window.location.origin);

// Interceptor para adicionar token de autorização
api.interceptors.request.use(
  async (config) => {
    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('[API] Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        hasToken: !!token
      });
    } else {
      console.warn('[API] Request sem token:', {
        method: config.method?.toUpperCase(),
        url: config.url
      });
    }
    return config;
  },
  (error) => {
    console.error('[API] Erro no interceptor de request:', error);
    return Promise.reject(error);
  }
);

// Interceptor para tratar 401 e refresh token
api.interceptors.response.use(
  (response) => {
    console.log('[API] Response:', {
      status: response.status,
      url: response.config.url
    });
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Log mais detalhado para debug
    console.error('[API] Erro na resposta:', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.message,
      data: error.response?.data,
      code: error.code,
      requestUrl: error.config?.baseURL + error.config?.url,
      isNetworkError: !error.response
    });

    // Se for erro de rede (Failed to fetch), logar mais detalhes
    if (!error.response && error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
      console.error('[API] Erro de rede - possíveis causas:');
      console.error('  - URL da API incorreta:', error.config?.baseURL);
      console.error('  - CORS bloqueado');
      console.error('  - Servidor não acessível');
      console.error('  - Certificado SSL inválido');
    }

    // Tratar 401 (Unauthorized) - token expirado ou inválido
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('[API] 401 detectado, tentando refresh token...');
      originalRequest._retry = true;

      try {
        const { refreshToken } = await import('../auth/oidcClient');
        await refreshToken();
        const newToken = await getAccessToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          console.log('[API] Token renovado, repetindo requisição...');
          return api(originalRequest);
        } else {
          console.warn('[API] Token não disponível após refresh');
          return Promise.reject(error);
        }
      } catch (refreshError) {
        console.error('[API] Erro ao renovar token:', refreshError);
        // Se refresh falhar, limpar usuário do storage
        // O router vai detectar que não está autenticado e redirecionar para login
        try {
          const { clearUser } = await import('../auth/oidcClient');
          await clearUser();
        } catch (e) {
          console.error('[API] Erro ao limpar usuário:', e);
        }
        return Promise.reject(refreshError);
      }
    }

    // Tratar 403 (Forbidden) - usuário não tem permissão ou token inválido
    // Deve redirecionar para login, similar ao 401
    if (error.response?.status === 403) {
      console.warn('[API] 403 (Forbidden) detectado - usuário não autorizado. Limpando sessão e redirecionando para login...');
      
      // Limpar usuário do storage para forçar novo login
      try {
        const { clearUser, signIn } = await import('../auth/oidcClient');
        await clearUser();
        
        // Redirecionar para login
        console.log('[API] Redirecionando para login...');
        await signIn();
      } catch (e) {
        console.error('[API] Erro ao limpar usuário e redirecionar para login:', e);
      }
      
      // Rejeitar o erro para que o código que chamou a API saiba que houve falha de autenticação
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Funções da API
export const getBootstrap = async () => {
  console.log('[API] getBootstrap chamado');
  try {
    const response = await api.get('/offline/bootstrap');
    
    // Validar se a resposta é um array
    if (!Array.isArray(response.data)) {
      console.error('[API] getBootstrap: resposta não é um array:', {
        type: typeof response.data,
        isString: typeof response.data === 'string',
        preview: typeof response.data === 'string' ? response.data.substring(0, 100) : response.data
      });
      throw new Error('Resposta da API não é um array de empresas');
    }
    
    console.log('[API] getBootstrap sucesso:', {
      dataLength: response.data.length,
      firstItem: response.data[0] ? { id: response.data[0].id || response.data[0].Id, nome: response.data[0].nome || response.data[0].Nome } : null
    });
    return response.data;
  } catch (error) {
    console.error('[API] getBootstrap erro:', error);
    throw error;
  }
};

export const postSync = async (payload) => {
  const response = await api.post('/offline/sync', payload);
  return response.data;
};

export const getAfericaoAberta = async (empresaId) => {
  try {
    const response = await api.get('/afericao/aberta', {
      params: { empresaId }
    });
    return response.data;
  } catch (error) {
    // Se retornar 204 (NoContent), não há aferição aberta
    if (error.response?.status === 204) {
      return null;
    }
    throw error;
  }
};

export const postCriarAfericao = async (payload) => {
  const response = await api.post('/afericao', payload);
  return response.data;
};

export const putAfericaoBico = async (afericaoId, numeroBico, formData) => {
  const response = await api.put(
    `/afericao/${afericaoId}/bico/${numeroBico}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

export const postFinalizarAfericao = async (afericaoId) => {
  const response = await api.post(`/afericao/${afericaoId}/finalizar`);
  return response.data;
};

export const getEmpresasProximas = async (latitude, longitude, raioMetros = 300) => {
  const response = await api.get('/empresa/proximas', {
    params: { latitude, longitude, raioMetros }
  });
  return response.data;
};

export const getEmpresaPorId = async (empresaId) => {
  const response = await api.get(`/empresa/${empresaId}`);
  return response.data;
};

export const getAfericaoPorId = async (afericaoId) => {
  const response = await api.get(`/afericao/${afericaoId}`);
  return response.data;
};

export const obterBico = async (afericaoId, numeroBico) => {
  const response = await api.get(`/afericao/${afericaoId}/bico/${numeroBico}`);
  return response.data;
};

export const deletarBico = async (afericaoId, numeroBico) => {
  const response = await api.delete(`/afericao/${afericaoId}/bico/${numeroBico}`);
  return response.data;
};

export const obterUltimasAfericoesPorBico = async (empresaId) => {
  const response = await api.get(`/afericao/ultimas-por-bico/${empresaId}`);
  return response.data;
};

